/**
 * Pure reducer for the IM window manager. Holds the registry of open windows,
 * their z-order, focus, and per-window geometry / minimized / maximized state.
 *
 * This module is deliberately free of React and of any IM/chat specifics — it's
 * a small desktop-style window manager. The window CONTENT (an IM conversation,
 * say) is an arbitrary React node stored opaquely on `render`; the manager never
 * looks inside it. That keeps window-management logic cleanly separated from
 * what a window shows.
 *
 * State shape:
 *   {
 *     windows: { [id]: WindowState },
 *     order:   [id, ...],   // creation order (stable; NOT z-order)
 *     focusedId: string|null,
 *     nextZ:   number,      // monotonically increasing z-index allocator
 *   }
 *
 * WindowState:
 *   { id, title, render, props, x, y, w, h, z,
 *     minimized, maximized, prevRect, options }
 *   - render(props) -> ReactNode   the content factory
 *   - props                        latest content props (merged by 'update')
 *   - prevRect {x,y,w,h}           remembered rect to restore to after maximize
 *   - options                      open-time opts (minWidth, minHeight, etc.)
 */

// Base z-index the windows occupy. Chosen to sit ABOVE the sesh banner (50) and
// below the reconnect/setup modals (theme.zIndex.modal + 10 ≈ 1310). Each focus
// bump increments from here; with a few windows we never approach the modal band.
export const WINDOW_Z_BASE = 1000;

export const initialWindowState = {
  windows: {},
  order: [],
  focusedId: null,
  nextZ: WINDOW_Z_BASE + 1,
};

// Default geometry for a freshly opened window when options omit a rect.
const DEFAULT_W = 320;
const DEFAULT_H = 380;

// Cascade offset so successive new windows don't stack exactly on top of each
// other. Wraps every 6 windows.
const CASCADE_STEP = 28;
const CASCADE_WRAP = 6;

export function windowReducer(state, action) {
  switch (action.type) {
    case 'open': {
      const { id, title, render, props, options } = action;
      // Re-opening an existing id focuses + un-minimizes it and refreshes its
      // content props/title rather than creating a duplicate.
      if (state.windows[id]) {
        return windowReducer(
          windowReducer(
            { ...state, windows: { ...state.windows, [id]: {
              ...state.windows[id],
              title: title ?? state.windows[id].title,
              render: render ?? state.windows[id].render,
              props: props ? { ...state.windows[id].props, ...props } : state.windows[id].props,
            } } },
            { type: 'restore', id }
          ),
          { type: 'focus', id }
        );
      }
      const n = state.order.length % CASCADE_WRAP;
      const w = options?.width ?? DEFAULT_W;
      const h = options?.height ?? DEFAULT_H;
      // Seed near the top-right, cascading down-left, clamped to keep it sane on
      // tiny viewports. The Window component re-clamps to the live viewport on
      // mount, so approximate placement here is fine.
      const vw = options?.viewportW ?? 1280;
      const x = options?.x ?? Math.max(16, vw - w - 24 - n * CASCADE_STEP);
      const y = options?.y ?? 72 + n * CASCADE_STEP;
      const win = {
        id,
        title: title ?? '',
        render: render ?? (() => null),
        props: props ?? {},
        x, y, w, h,
        z: state.nextZ,
        minimized: false,
        maximized: false,
        prevRect: null,
        options: options ?? {},
      };
      return {
        ...state,
        windows: { ...state.windows, [id]: win },
        order: [...state.order, id],
        focusedId: id,
        nextZ: state.nextZ + 1,
      };
    }

    case 'close': {
      const { id } = action;
      if (!state.windows[id]) return state;
      const windows = { ...state.windows };
      delete windows[id];
      const order = state.order.filter((x) => x !== id);
      // If we closed the focused window, focus the top-most remaining one.
      let focusedId = state.focusedId;
      if (focusedId === id) {
        focusedId = topMostId(windows);
      }
      return { ...state, windows, order, focusedId };
    }

    case 'focus': {
      const { id } = action;
      const win = state.windows[id];
      if (!win) return state;
      // Already focused AND already top-most → no-op (avoids churning nextZ).
      if (state.focusedId === id && win.z === maxZ(state.windows)) return state;
      return {
        ...state,
        windows: { ...state.windows, [id]: { ...win, z: state.nextZ } },
        focusedId: id,
        nextZ: state.nextZ + 1,
      };
    }

    case 'minimize': {
      const { id } = action;
      const win = state.windows[id];
      if (!win || win.minimized) return state;
      const windows = { ...state.windows, [id]: { ...win, minimized: true } };
      // Minimizing the focused window hands focus to the next top-most.
      const focusedId = state.focusedId === id ? topMostId(windows, id) : state.focusedId;
      return { ...state, windows, focusedId };
    }

    case 'restore': {
      // Un-minimize (does NOT change maximized). Focus handled by caller/open.
      const { id } = action;
      const win = state.windows[id];
      if (!win || !win.minimized) return state;
      return {
        ...state,
        windows: { ...state.windows, [id]: { ...win, minimized: false } },
      };
    }

    case 'maximize': {
      // Toggle maximized. On maximize, remember the current rect to restore to.
      const { id } = action;
      const win = state.windows[id];
      if (!win) return state;
      const next = win.maximized
        ? { ...win, maximized: false }
        : { ...win, maximized: true, prevRect: { x: win.x, y: win.y, w: win.w, h: win.h } };
      return { ...state, windows: { ...state.windows, [id]: next } };
    }

    case 'setRect': {
      // Persist a drag/resize result. Ignored while maximized (the maximized
      // geometry is computed by the view, not stored here).
      const { id, rect } = action;
      const win = state.windows[id];
      if (!win || win.maximized) return state;
      return {
        ...state,
        windows: {
          ...state.windows,
          [id]: {
            ...win,
            x: rect.x ?? win.x,
            y: rect.y ?? win.y,
            w: rect.w ?? win.w,
            h: rect.h ?? win.h,
          },
        },
      };
    }

    case 'update': {
      // Merge new content props, an optional new title, and optional option
      // overrides (e.g. { attention } for the blink) into a window.
      const { id, props, title, options } = action;
      const win = state.windows[id];
      if (!win) return state;
      return {
        ...state,
        windows: {
          ...state.windows,
          [id]: {
            ...win,
            props: props ? { ...win.props, ...props } : win.props,
            title: title ?? win.title,
            options: options ? { ...win.options, ...options } : win.options,
          },
        },
      };
    }

    default:
      return state;
  }
}

// Highest z value among the windows (0 if none).
function maxZ(windows) {
  let m = 0;
  for (const id in windows) if (windows[id].z > m) m = windows[id].z;
  return m;
}

// Id of the top-most NON-minimized window, optionally excluding `exceptId`.
// null if none qualify.
function topMostId(windows, exceptId) {
  let best = null;
  let bestZ = -1;
  for (const id in windows) {
    if (id === exceptId) continue;
    const w = windows[id];
    if (w.minimized) continue;
    if (w.z > bestZ) {
      bestZ = w.z;
      best = id;
    }
  }
  return best;
}
